import React from 'react';
import PlayIcon from './assets/PlayIcon';
import PauseIcon from './assets/PauseIcon';
import FullScreenIcon from './assets/FullScreenIcon';
import PlaybackIcon from './assets/PlaybackIcon';
import {
  LTIconButton,
  LTActionMenu,
} from '@lambdatestincprivate/lt-components';
import { RotateIcon } from '../../assets/rotateIcon';
import DownloadIcon from '../../assets/primer-download';

const formatTime = (seconds) => {
  if (isNaN(seconds)) {
    return '00:00';
  }
  const date = new Date(seconds * 1000);
  const hh = date.getUTCHours();
  const mm = date.getUTCMinutes();
  const ss = date
    .getUTCSeconds()
    .toString()
    .padStart(2, '0');
  if (hh) {
    const minutesToShow = `${mm.toString().padStart(2, '0')}`;
    return {
      time: `${hh}:${minutesToShow}:${ss}`,
      label: `${hh} hours, ${minutesToShow} minutes, ${ss} seconds`,
    };
  }
  return { time: `${mm}:${ss}`, label: `${mm} minutes, ${ss} seconds` };
};

const playbackRateOptions = [
  { name: '0.25x', value: 0.25 },
  { name: '0.5x', value: 0.5 },
  { name: '0.75x', value: 0.75 },
  { name: 'Normal', value: 1 },
  { name: '1.25x', value: 1.25 },
  { name: '1.5x', value: 1.5 },
  { name: '1.75x', value: 1.75 },
  { name: '2x', value: 2 },
];

const CustomVideoControls = (props) => {
  const {
    videoRef,
    handleVideoDownload,
    containerStyles,
    controls,
    onManualVideoProgress,
  } = props;

  const {
    isPlaying,
    ready,
    progress,
    bufferedProgress,
    playbackRate,
    togglePlay,
    setPlayerState,
    duration,
    updatePlaybackRate,
    handleRotation,
    allowRotation,
    width,
  } = controls || {};

  const playbackRateOptionsUpdated = playbackRateOptions.map(
    ({ name, value }) => ({
      name: name,
      onClickHandler: () => {
        updatePlaybackRate(value);
      },
      variant: 'default',
      id: `playback-${name}`,
    })
  );

  const handleProgressChange = (e) => {
    if (!e.target?.value) {
      return;
    }

    const changedProgressed = parseFloat(e.target.value);

    // seek value < 1 is considered as fraction by seekTo
    const correctedProgress =
      changedProgressed < 1 ? Math.round(changedProgressed) : changedProgressed;

    setPlayerState((prevState) => ({
      ...prevState,
      progress: correctedProgress,
    }));
    if (videoRef.current) {
      onManualVideoProgress();
      videoRef.current.seekTo(correctedProgress);
    }
  };

  const sliderKeyDownHandler = (e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      setPlayerState((prevState) => ({
        ...prevState,
        progress: Math.min(progress + 2, duration),
      }));
      if (videoRef.current) {
        videoRef.current.seekTo(progress + 2);
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setPlayerState((prevState) => ({
        ...prevState,
        progress: Math.max(progress - 2, 0),
      }));
      if (videoRef.current) {
        videoRef.current.seekTo(progress - 2);
      }
    }
  };

  const playPauseIcon = isPlaying ? PauseIcon : PlayIcon;
  const { time: progressTime = '', label: progressLabel = '' } = formatTime(
    progress
  );
  const { time: durationTime = '', label: durationLabel = '' } = formatTime(
    duration
  );

  return (
    <div
      className="ltp-custom-controls"
      style={{ ...containerStyles, opacity: ready ? 1 : 0.8, width: width, zIndex: 98 }}
      onClick={(e) => e.stopPropagation()}
    >
      <LTIconButton
        ariaLabel={isPlaying ? 'Pause Video' : 'Play Video'}
        data-testid="video-play-pause-btn"
        onClick={togglePlay}
        variant={LTIconButton.IconButtonVariants.INVISIBLE}
        icon={playPauseIcon}
        size={LTIconButton.Size.SMALL}
      />
      <span className="ltp-time-current">{progressTime}</span>
      <div
        className="progress-bar-container"
        onClick={(e) => e.preventDefault()}
      >
        <input
          aria-label="Seek Video"
          autoComplete="on"
          data-testid="video-seek-slider"
          id="range-input"
          type="range"
          value={progress}
          min={0}
          step={0.1}
          max={duration}
          onChange={handleProgressChange}
          onKeyDown={sliderKeyDownHandler}
          style={{ opacity: 1 }}
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuetext={`${progressLabel} of ${durationLabel}`}
        />
        <div
          className="ltp-buffered-progress"
          style={{ width: `${(bufferedProgress / duration) * 100}%` }}
        ></div>
        <div
          className="ltp-seeked-progress"
          style={{ width: `${(progress / duration) * 100}%` }}
        ></div>
      </div>
      <span className="ltp-time-total">{durationTime}</span>
      <div className="ltp-actions">
        <LTActionMenu
          activeId={
            playbackRateOptions.find(
              (playbackRateOption) => playbackRateOption.value === playbackRate
            )?.name
          }
          anchorButtonSize={LTIconButton.Size.SMALL}
          anchorType={LTActionMenu.AnchorType.ICON}
          data-testid="video-playback-menu"
          iconButtonVariant={LTIconButton.IconButtonVariants.INVISIBLE}
          id="playback-menu"
          leadingIcon={PlaybackIcon}
          menuSize={'150px'}
          options={[
            {
              list: [...playbackRateOptionsUpdated],
            },
          ]}
          selectionVariant={LTActionMenu.SelectionVariant.SINGLE}
          title="Playback Speed"
        />
        <LTIconButton
          ariaLabel="Download Video"
          data-testid="video-download-btn"
          icon={DownloadIcon}
          onClick={() => {
            handleVideoDownload();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              e.preventDefault();
              handleVideoDownload();
            }
          }}
          variant={LTIconButton.IconButtonVariants.INVISIBLE}
          size={LTIconButton.Size.SMALL}
        />
        {allowRotation && (
          <LTIconButton
            ariaLabel="Rotate Left"
            data-testid="video-rotate-btn"
            icon={RotateIcon}
            onClick={() => handleRotation('left')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                e.preventDefault();
                handleRotation('left');
              }
            }}
            size={LTIconButton.Size.SMALL}
            style={{
              transform: 'scale(-1, 1)',
            }}
            variant={LTIconButton.IconButtonVariants.INVISIBLE}
          />
        )}
        <LTIconButton
          ariaLabel="Fullscreen"
          data-testid="video-fullscreen-btn"
          icon={FullScreenIcon}
          onClick={() => {
            const internalPlayer = videoRef.current?.getInternalPlayer();
            if (internalPlayer) {
              internalPlayer.requestFullscreen().catch((err) => {
                console.log('Fullscreen request failed:', err);
              });
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              e.preventDefault();
              const internalPlayer = videoRef.current?.getInternalPlayer();
              if (internalPlayer) {
                internalPlayer.requestFullscreen().catch((err) => {
                  console.log('Fullscreen request failed:', err);
                });
              }
            }
          }}
          size={LTIconButton.Size.SMALL}
          variant={LTIconButton.IconButtonVariants.INVISIBLE}
        />
      </div>
    </div>
  );
};

export default CustomVideoControls;
